import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Icon,
  Input,
  SpaceBetween,
  Table,
  TextContent,
} from '@amzn/awsui-components-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChallengePropAction, useCreateChallenge } from '../../../../store/create-challenge.context';
import { Challenge } from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { isEmailValid } from '../../../../utils/string.utils';
import { ConfirmModal } from '../../../common/ConfirmModal';
import './Collaborators.scss';

interface ChallengeCollaboratorsDetailProps {
  challenge: Challenge;
}

const ChallengeCollaboratorsDetail: React.FC<ChallengeCollaboratorsDetailProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const { editMode, editedChallenge, handleUpdateChallengeProp } = useCreateChallenge();
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [collaborator, setCollaborator] = useState('');

  const [confirmTransferVisible, setConfirmTransferVisible] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');

  useEffect(() => {
    updateCollaborators();
  }, [editedChallenge, challenge]);

  const updateCollaborators = () => {
    const currentCollaborators: string[] = [];
    const currentChallenge = editMode && editedChallenge ? editedChallenge : challenge;
    currentCollaborators.push(currentChallenge.props.owner || '');
    currentChallenge.props.maintainers.forEach((maintainer) => {
      if (maintainer !== currentChallenge.props.owner) {
        currentCollaborators.push(maintainer);
      }
    });
    setCollaborators(currentCollaborators);
  };
  const addCollaborator = () => {
    const currentMaintainers: string[] = collaborators.filter((c) => c !== editedChallenge?.props.owner);
    currentMaintainers.push(collaborator);

    handleUpdateChallengeProp(ChallengePropAction.MAINTAINERS, currentMaintainers);
    setCollaborator('');
  };

  const removeCollaborator = (email: string) => {
    const currentMaintainers: string[] = collaborators.filter((c) => c !== email && c !== editedChallenge?.props.owner);
    handleUpdateChallengeProp(ChallengePropAction.MAINTAINERS, currentMaintainers);
  };

  const handleTransferOwnership = () => {
    const newOwner = newOwnerEmail.trim();
    if (isEmailValid(newOwner)) {
      handleUpdateChallengeProp(ChallengePropAction.OWNER, newOwner);
      setConfirmTransferVisible(false);
      setNewOwnerEmail('');
    }
    return;
  };

  const activeChallenge = editMode && editedChallenge ? editedChallenge : challenge;

  return (
    <Container
      header={
        <Header variant="h2">
          {t(i18nKeys.challenges.challengeDetails.headings.collaborators)} ({collaborators?.length || 0})
        </Header>
      }>
      <SpaceBetween size="m">
        {editMode && (
          <div className="collaborators-header">
            <SpaceBetween size="s">
              <TextContent>
                <strong>{t(i18nKeys.challenges.challengeDetails.text.addMaintainer)}</strong>
              </TextContent>
              <FormField label="Email">
                <SpaceBetween size="s" direction="horizontal">
                  <div style={{ width: '420px' }}>
                    <Input
                      value={collaborator}
                      onChange={({ detail }) => setCollaborator(detail.value)}
                      placeholder={'alias@domain.com'}
                      inputMode="email"
                      type="email"
                      data-testid="challenge-collaborator-email-input"
                    />
                  </div>
                  <Button id='challenge-collaborator-add-maintainer' data-testid='challenge-collaborator-add-maintainer' onClick={addCollaborator} variant="primary">
                    {t(i18nKeys.challenges.challengeDetails.text.addMaintainer)}
                  </Button>
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </div>
        )}
        <Table
          variant="full-page"
          columnDefinitions={
            editMode
              ? [
                  {
                    id: 'email',
                    header: t(i18nKeys.challenges.challengeDetails.table.titles.email),
                    cell: (item) => item || '-',
                    sortingField: 'name',
                  },
                  {
                    id: 'role',
                    header: t(i18nKeys.challenges.challengeDetails.table.titles.role),
                    sortingField: 'role',
                    cell: (item) => {
                      if (activeChallenge.props.owner === item) {
                        return (
                          <SpaceBetween size="xxs" direction="horizontal">
                            <Icon name="star-filled" variant="warning" />
                            {t(i18nKeys.challenges.challengeDetails.table.role.owner)}
                          </SpaceBetween>
                        );
                      } else {
                        return t(i18nKeys.challenges.challengeDetails.table.role.maintainer);
                      }
                    },
                  },
                  {
                    id: 'actions',
                    header: t(i18nKeys.general.actions),
                    sortingField: 'actions',
                    cell: (item) => {
                      if (item !== activeChallenge.props.owner) {
                        return (
                          <Button
                            onClick={() => removeCollaborator(item)}
                            variant="inline-link"
                            iconName='remove'>
                            {t(i18nKeys.general.remove)}
                          </Button>
                        );
                      } else {
                        return (
                          <Button
                            onClick={() => setConfirmTransferVisible(true)}
                            iconName='upload-download'
                            variant="inline-link"
                            >
                            {t(i18nKeys.challenges.challengeDetails.buttons.transferOwnership)}
                          </Button>
                        );
                      }
                    },
                  },
                ]
              : [
                  {
                    id: 'email',
                    header: t(i18nKeys.challenges.challengeDetails.table.titles.email),
                    cell: (item) => {
                      if (activeChallenge.props.owner === item) {
                        return (
                          <SpaceBetween direction="horizontal" size="xxs">
                            {item} <Icon name="star-filled" variant="warning" />
                          </SpaceBetween>
                        );
                      }
                      return item;
                    },
                    sortingField: 'name',
                    width: 485,
                  },
                  {
                    id: 'role',
                    header: t(i18nKeys.challenges.challengeDetails.table.titles.role),
                    sortingField: 'role',
                    width: 200,
                    cell: (item) => {
                      if (activeChallenge.props.owner === item) {
                        return t(i18nKeys.challenges.challengeDetails.table.role.owner);
                      } else {
                        return t(i18nKeys.challenges.challengeDetails.table.role.maintainer);
                      }
                    },
                  },
                ]
          }
          items={collaborators}
          loadingText={t(i18nKeys.challenges.challengeDetails.table.loadingCollaborators)}
          resizableColumns
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t(i18nKeys.challenges.challengeDetails.table.emptyCollaboratorsState)}</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                {t(i18nKeys.challenges.challengeDetails.table.emptyCollaboratorsSubtitle)}
              </Box>
            </Box>
          }
        />
      </SpaceBetween>
      <ConfirmModal
        title={t(i18nKeys.challenges.challengeDetails.buttons.transferOwnership)}
        message={`${t(i18nKeys.challenges.challengeDetails.messages.confirmNewOwnersEmail)} ${
          activeChallenge?.props?.owner
        }`}
        label={t(i18nKeys.challenges.challengeDetails.messages.newOwnerEmail)}
        visible={confirmTransferVisible}
        textInput={newOwnerEmail}
        onTextInput={setNewOwnerEmail}
        onConfirm={handleTransferOwnership}
        onCancel={() => setConfirmTransferVisible(false)}
        confirmBtnLabel={t(i18nKeys.challenges.challengeDetails.text.addMaintainer)}
      />
    </Container>
  );
};

export default ChallengeCollaboratorsDetail;
